import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import arraySort from 'array-sort'
import { injectIntl } from 'react-intl'
import Select from 'react-select'
import styled from 'styled-components'
import CobaltHyperlink from '../typography/CobaltHyperlink'

import KeywordSearch from './SearchInputs/KeywordSearch'
import SearchArea from './SearchArea'
import QuestStatusSelect from './QuestStatusSelect'
import CourseSelect from './CourseSelect'
import WrappedSearchFilter from '../HOC/WrappedSearchFilter'
import SingleSearchArea from './SingleSearchArea'

import { fetchQuests } from '../../actions/quests'
import {
  setAuthorFilters,
  setTagFilters,
  setKeywordFilter,
  setDraftFilter,
  setStatusFilter,
  setPrimarySubjectFilter,
  setAssignedToFilters,
  setTargetGradeLevelFilter,
  setCourseFilter,
  setLevelFilter as setLevelFilterAction,
  setReviewTrackingStatus,
  clearSearchFilters
} from '../../actions/questsfilters'
import { messages as labels } from '../../utils/messages/label'
import { messages as placeholders } from '../../utils/messages/placeholder'
import { getqdUsers, assignedUsers } from '../../selectors/users'
import { courseLevels } from '../../selectors/courselevels'
import { mapLevels } from '../../utils/levels'
import { reviewTracking } from '../../constants/reviewtracking'
import { isOeReviewer, isQuestManager } from '../../utils/user'
import { questsSearchFiltersAreDirty } from '../../utils/quests'

const ReviewTrackingStatusDescription = styled.div`
  color: #666666;
  font-size: 11px;
  font-weight: 300;
  line-height: 16px;
  padding-top: 8px;
`

const ClearSearch = styled(CobaltHyperlink)`
  position: absolute;
  top: 0;
  right: 0;
  padding: inherit;
`

class QuestsSearch extends Component {
  constructor(props) {
    super(props)
    this.state = {
      keywordSearched: Boolean(this.props.keywordFilter),
      questStatusSelectActive:
        this.props.statusFilters.length > 0 || this.props.draftFilterSelected,
      // not ideal - need this key to remount children when clearing search options, to clear children states
      key: Math.random()
    }
    this.onSearchClickBound = this.onSearchClick.bind(this)
    this.onKeywordKeyPressBound = this.onKeywordKeyPress.bind(this)
    this.onKeywordFilterChangeBound = this.onKeywordFilterChange.bind(this)
    this.setStatusFilterBound = this.setStatusFilter.bind(this)
    this.onKeywordSearchClickBound = this.onKeywordSearchClick.bind(this)
    this.onClearKeywordSearchClickBound =
      this.onClearKeywordSearchClick.bind(this)
    this.keywordSearchInput = React.createRef()
    this.statusFilterRef = React.createRef()
    this.courseFilterRef = React.createRef()
    this.levelFilterRef = React.createRef()
    this.onSetCourseFilterBound = this.onSetCourseFilter.bind(this)
    this.setLevelFilterBound = this.setLevelFilter.bind(this)
    this.onSetReviewTrackingFilterBound =
      this.onSetReviewTrackingFilter.bind(this)
    this.onClearSearchBound = this.onClearSearch.bind(this)
  }

  render() {
    const {
      dispatch,
      isFetchingUsers,
      tags,
      isFetchingTags,
      authorFilters,
      tagFilters,
      draftFilterSelected,
      allStatusChecked,
      statusFilters,
      keywordFilter,
      primarySubjectFilters,
      isFetchingSubjects,
      primarySubjects,
      assignedToFilters,
      qdUsers,
      assignedUsers,
      gradeLevels,
      isFetchingGrades,
      gradeFilters,
      intl,
      courses,
      courseFilter,
      levelFilters,
      levels,
      reviewTrackingStatus
    } = this.props

    const isDirty = questsSearchFiltersAreDirty(this.props)

    const labelMessages = {
      author: intl.formatMessage(labels.author),
      tags: intl.formatMessage(labels.tags),
      primarySubject: intl.formatMessage(labels.primarySubject),
      assignedTo: intl.formatMessage(labels.assignedTo),
      status: intl.formatMessage(labels.status),
      targetGradeLevel: intl.formatMessage(labels.targetGradeLevel)
    }

    const placeholderMessages = {
      selectAuthors: intl.formatMessage(placeholders.selectAuthors),
      selectTags: intl.formatMessage(placeholders.selectTags),
      selectSubject: intl.formatMessage(placeholders.selectSubject),
      selectColleagues: intl.formatMessage(placeholders.selectColleagues),
      selectGrades: intl.formatMessage(placeholders.selectGrades)
    }

    return (
      <div
        className="quests-search"
        style={{ position: 'relative' }}
        key={this.state.key}
      >
        <KeywordSearch
          keywordFilter={keywordFilter}
          keywordSearchRef={this.keywordSearchInput}
          onKeywordKeyPress={this.onKeywordKeyPressBound}
          onKeywordFilterChange={this.onKeywordFilterChangeBound}
          keywordSearched={this.state.keywordSearched}
          onClearKeywordSearchClick={this.onClearKeywordSearchClickBound}
          onKeywordSearchClick={this.onKeywordSearchClickBound}
        />
        {isDirty && (
          <ClearSearch onClick={this.onClearSearchBound}>Clear All</ClearSearch>
        )}

        <div className="filter-by-container">
          <div className="caption1 filter-by-caption">Filter by</div>
          <div className="area inputs-wrapper">
            <SearchArea
              headerClass="h5"
              dispatch={dispatch}
              label={labelMessages.author}
              area="author"
              placeholder={placeholderMessages.selectAuthors}
              values={authorFilters}
              options={arraySort(this.mapList(qdUsers), ['sort'])}
              filterAction={setAuthorFilters}
              loading={isFetchingUsers}
              fetchAction={fetchQuests}
            />

            <SearchArea
              headerClass="h5"
              dispatch={dispatch}
              label={labelMessages.assignedTo}
              area="assigned-to"
              placeholder={placeholderMessages.selectColleagues}
              values={assignedToFilters}
              options={arraySort(this.mapList(assignedUsers, ['sort']))}
              filterAction={setAssignedToFilters}
              loading={isFetchingUsers}
              fetchAction={fetchQuests}
            />

            <SearchArea
              headerClass="h5"
              dispatch={dispatch}
              label={labelMessages.tags}
              area="tags"
              placeholder={placeholderMessages.selectTags}
              values={tagFilters}
              options={arraySort(this.mapList(tags), ['sort'])}
              filterAction={setTagFilters}
              loading={isFetchingTags}
              fetchAction={fetchQuests}
            />

            <SearchArea
              headerClass="h5"
              dispatch={dispatch}
              label={labelMessages.primarySubject}
              area="subjects"
              placeholder={placeholderMessages.selectSubject}
              values={primarySubjectFilters}
              options={arraySort(this.mapList(primarySubjects), ['sort'])}
              filterAction={setPrimarySubjectFilter}
              loading={isFetchingSubjects}
              fetchAction={fetchQuests}
            />

            <SearchArea
              headerClass="h5"
              dispatch={dispatch}
              label={labelMessages.targetGradeLevel}
              area="grades"
              placeholder={placeholderMessages.selectGrades}
              values={gradeFilters}
              options={arraySort(this.mapList(gradeLevels), ['sort'])}
              filterAction={setTargetGradeLevelFilter}
              loading={isFetchingGrades}
              fetchAction={fetchQuests}
            />

            <WrappedSearchFilter
              area="status"
              active={
                this.props.statusFilters.length > 0 ||
                this.props.draftFilterSelected
              }
              label="Status"
              wrappedRef={this.statusFilterRef}
            >
              <QuestStatusSelect
                statusFilterRef={this.statusFilterRef}
                setStatusFilter={this.setStatusFilterBound}
                draftFilterSelected={draftFilterSelected}
                statusFilters={statusFilters}
                allStatusChecked={allStatusChecked}
              />
            </WrappedSearchFilter>

            {(isOeReviewer(this.props.user) ||
              isQuestManager(this.props.user)) && (
              <React.Fragment>
                <SingleSearchArea
                  headerClass="h5"
                  dispatch={dispatch}
                  label="Review Tracking"
                  area="review-tracking"
                  placeholder="Select Review Tracking"
                  value={reviewTrackingStatus}
                  options={arraySort(reviewTracking, ['sort'])}
                  handleSelectChange={this.onSetReviewTrackingFilterBound}
                />
                <div style={{ marginBottom: '16px' }}>
                  {reviewTrackingStatus && (
                    <ReviewTrackingStatusDescription>
                      {
                        reviewTracking.find(
                          rt => rt.id === reviewTrackingStatus.id
                        ).description
                      }
                    </ReviewTrackingStatusDescription>
                  )}
                </div>
              </React.Fragment>
            )}
            {courses.length > 0 &&
              <WrappedSearchFilter
                area="courses"
                active={Boolean(this.props.courseFilter)}
                label="Intended Journey"
                wrappedRef={this.courseFilterRef}
              >
                <CourseSelect
                  courseFilterRef={this.courseFilterRef}
                  course={courseFilter ? courseFilter : null}
                  courses={courses}
                  onCourseChange={this.onSetCourseFilterBound}
                  labelTruncationLength={99999}
                  placeholder="Select journey"
                />
              </WrappedSearchFilter>
            }
            {courseFilter &&
              <WrappedSearchFilter
                area="levels"
                active={this.props.levelFilters.length > 0}
                disabled={levels.length === 0}
                disabledText="No levels are available for this journey."
                label="Intended Level"
                wrappedRef={this.levelFilterRef}
              >
                <Select
                  multi
                  ref={this.levelFilterRef}
                  searchable
                  options={mapLevels(levels, 22)}
                  value={levelFilters}
                  onChange={this.setLevelFilterBound}
                  placeholder="Select level"
                  matchProp="label"
                />
              </WrappedSearchFilter>
            }
          </div>
        </div>
      </div>
    )
  }

  onKeywordFilterChange(e) {
    this.props.dispatch(setKeywordFilter(e.currentTarget.value))
  }

  onKeywordSearchClick() {
    this.setState(() => {
      return {
        keywordSearched: true
      }
    })
    this.onSearchClickBound()
  }

  onKeywordKeyPress(e) {
    if (
      this.props.keywordFilter.length > 0 &&
      (e.which === 13 || e.charCode === 13)
    ) {
      this.onKeywordSearchClickBound()
      this.keywordSearchInput.current.blur()
    }
  }

  onClearKeywordSearchClick() {
    this.props.dispatch(setKeywordFilter(''))
    this.setState(() => {
      return {
        keywordSearched: false
      }
    })
    this.onSearchClickBound()
  }

  onSearchClick() {
    this.props.dispatch(fetchQuests(1, true))
  }

  setStatusFilter(status) {
    if (status.value.length === 0) {
      this.props.dispatch(setDraftFilter(null))
      this.props.dispatch(setStatusFilter([]))
      this.onSearchClickBound()
    } else if (status.value.indexOf(1) > -1) {
      this.props.dispatch(setDraftFilter(1))
      this.props.dispatch(setStatusFilter([]))
      this.onSearchClickBound()
    } else {
      this.props.dispatch(setStatusFilter(status.value))
      this.props.dispatch(setDraftFilter(null))
      this.onSearchClickBound()
    }
  }

  mapList(arr) {
    return arr.map(function (el) {
      return {
        value: el.id,
        label: el.name,
        sort: el.sort ? el.sort : el.name.trim().toLowerCase()
      }
    })
  }

  async onSetCourseFilter(e) {
    await this.props.dispatch(setCourseFilter(e ? e : null))
    this.onSearchClickBound()
  }

  async setLevelFilter(event) {
    this.props.dispatch(
      setLevelFilterAction(
        event.map(function (e) {
          return e.value
        })
      )
    )
    this.props.dispatch(this.onSearchClickBound)
  }

  async onSetReviewTrackingFilter(value) {
    await this.props.dispatch(setReviewTrackingStatus(value))
    this.props.dispatch(this.onSearchClickBound)
  }

  async onClearSearch() {
    const { dispatch } = this.props
    await dispatch(clearSearchFilters())
    await this.setState(() => ({
      keywordSearched: false,
      // not ideal, but much less cumbersome than manually clearing the state of each SearchArea/Select input
      key: Math.random()
    }))
    dispatch(this.onSearchClickBound)
  }
}

QuestsSearch.propTypes = {
  dispatch: PropTypes.func,
  qdUsers: PropTypes.array.isRequired,
  assignedUsers: PropTypes.array.isRequired,
  isFetchingUsers: PropTypes.bool,
  tags: PropTypes.array.isRequired,
  isFetchingTags: PropTypes.bool,
  authorFilters: PropTypes.array,
  tagFilters: PropTypes.array,
  statusFilters: PropTypes.array,
  keywordFilter: PropTypes.string,
  draftFilterSelected: PropTypes.bool,
  allStatusChecked: PropTypes.bool,
  primarySubjectFilters: PropTypes.array,
  primarySubjects: PropTypes.array,
  isFetchingSubjects: PropTypes.bool,
  assignedToFilters: PropTypes.array,
  gradeLevels: PropTypes.array,
  isFetchingGrades: PropTypes.bool,
  gradeFilters: PropTypes.array,
  intl: PropTypes.object.isRequired,
  courses: PropTypes.array,
  courseFilter: PropTypes.object,
  levelFilters: PropTypes.array,
  levels: PropTypes.array,
  reviewTrackingStatus: PropTypes.object,
  user: PropTypes.object.isRequired
}

export const mapStateToProps = state => ({
  users: state.users.items,
  qdUsers: getqdUsers(state),
  assignedUsers: assignedUsers(state),
  isFetchingUsers: state.users.isFetching,
  tags: state.tags.items,
  isFetchingTags: state.tags.isFetching,
  authorFilters: state.questsfilters.authors_id_in || [],
  tagFilters: state.questsfilters.questtags_id_in || [],
  draftFilterSelected:
    state.questsfilters.reviewing_compiled_quest_state_null !== null,
  statusFilters: state.questsfilters.reviewing_compiled_quest_state_in || [],
  keywordFilter: state.questsfilters.name_or_description_cont || '',
  allStatusChecked:
    !state.questsfilters.reviewing_compiled_quest_state_null &&
    state.questsfilters.reviewing_compiled_quest_state_in.length === 0,
  primarySubjects: state.subjects.items,
  primarySubjectFilters: state.questsfilters.quest_subject_id_in || [],
  isFetchingSubjects: state.subjects.isFetching,
  assignedToFilters: state.questsfilters.assigned_to_id_in,
  gradeLevels: state.gradelevels.items,
  isFetchingGrades: state.gradelevels.isFetching,
  gradeFilters: state.questsfilters.grade_levels_id_in,
  courses: state.mockcourses.items,
  courseFilter: state.questsfilters.mock_course_id_in || null,
  levelFilters: state.questsfilters.mock_level_id_in || [],
  levels: courseLevels(state, state.questsfilters.mock_course_id_in),
  reviewTrackingStatus: state.questsfilters.reviewTrackingStatus,
  user: state.user.user
})

export default injectIntl(connect(mapStateToProps)(QuestsSearch))
export { QuestsSearch as QuestsSearchDisconnected } // pure component - for tests



// WEBPACK FOOTER //
// ./src/components/mini/QuestsSearch.js